/**
 * Midlife LF Trial LP Welcome Kit buybox (CNV-6094) — next charge date, breakdown flavor, free water bottle.
 *
 * The bottle is added as its own one-time line carrying the gift property from the section (default
 * `_offer: free-bottle`), which the Function Studio "Free water bottle" discount matches to make it $0.
 * The variant pack form's addToCart is wrapped so the bottle is added after the product and before the
 * redirect to checkout; one bottle is kept while a Welcome Kit line (`_offer: welcome-kit-XX`) is in the cart.
 */

(function () {
  if (window.__ctrMlLfWelcomeKitInitialized) return;
  window.__ctrMlLfWelcomeKitInitialized = true;

  const GIFT_ROOT_SELECTOR = "[data-ctr-ml-lf-trial-lp-buybox][data-ctr-welcome-kit-gift-variant]";
  const PANEL_SELECTOR = "[data-ctr-welcome-kit]";
  const FORM_SELECTOR = ".ctr-midlife-lf-trial-lp-product-buybox__variant-pack .variant-pack-form";
  const SELECTED_FLAVOR_SELECTOR =
    ".ctr-midlife-lf-trial-lp-product-buybox__flavor.is-selected .ctr-midlife-lf-trial-lp-product-buybox__flavor-name";

  let giftPromise = null;
  let giftUnavailable = false;
  const patchedForms = new WeakSet();

  function getGiftRoot() {
    return document.querySelector(GIFT_ROOT_SELECTOR);
  }

  function getGiftConfig(root) {
    const data = root?.dataset;
    if (!data?.ctrWelcomeKitGiftVariant) return null;
    return {
      variantId: Number(data.ctrWelcomeKitGiftVariant),
      giftKey: data.ctrWelcomeKitGiftKey || "_offer",
      giftValue: data.ctrWelcomeKitGiftValue || "free-bottle",
      offerKey: data.ctrWelcomeKitOfferKey || "_offer",
      offerValue: data.ctrWelcomeKitOfferValue || "",
    };
  }

  function getCheckoutUrl() {
    return window.Shopify?.routes?.root ? `${window.Shopify.routes.root}checkout` : "/checkout";
  }

  function formatNextChargeDate(days) {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }

  function syncNextChargeDate(panel) {
    const days = parseInt(panel.dataset.trialDays, 10);
    const label = formatNextChargeDate(Number.isFinite(days) ? days : 30);
    panel.querySelectorAll("[data-ctr-welcome-kit-next-date]").forEach((el) => {
      el.textContent = label;
    });
  }

  // The breakdown's tin rows name the selected flavor; the pills toggle `is-selected` through Alpine.
  function watchFlavor(panel) {
    const targets = panel.querySelectorAll("[data-ctr-welcome-kit-flavor]");
    if (!targets.length) return;

    const sync = () => {
      const name = panel.querySelector(SELECTED_FLAVOR_SELECTOR);
      const text = name?.textContent?.replace(/\s+/g, " ").replace(/sold out$/i, "").trim();
      if (!text) return;
      targets.forEach((target) => {
        if (target.textContent !== text) target.textContent = text;
      });
    };

    new MutationObserver(sync).observe(panel, {
      subtree: true,
      attributes: true,
      attributeFilter: ["class"],
    });
    sync();
  }

  async function fetchCart() {
    const response = await fetch("/cart.js", { headers: { Accept: "application/json" } });
    return response.json();
  }

  function isGiftLine(item, config) {
    return item?.properties?.[config.giftKey] === config.giftValue;
  }

  function isWelcomeKitLine(item, config) {
    return Boolean(config.offerValue) && !isGiftLine(item, config) && item?.properties?.[config.offerKey] === config.offerValue;
  }

  async function requestGift(root) {
    const config = getGiftConfig(root);
    if (!config || giftUnavailable) return;

    try {
      const cart = await fetchCart();
      const items = cart?.items || [];
      if (!items.some((item) => isWelcomeKitLine(item, config))) return;
      if (items.some((item) => isGiftLine(item, config))) return;

      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          id: config.variantId,
          quantity: 1,
          properties: { [config.giftKey]: config.giftValue },
        }),
      });
      // 422 = sold out / not purchasable: stop retrying so the cart refresh below can't loop.
      if (!response.ok) {
        giftUnavailable = true;
        return;
      }
      await window.Alpine?.store("cart")?.getCartData?.();
    } catch (error) {
      console.error("Welcome Kit: failed to add the water bottle", error);
    }
  }

  // Shares one in-flight request, so add to cart waits for a bottle the cart listener already started.
  function addGift(root) {
    if (!giftPromise) {
      giftPromise = requestGift(root || getGiftRoot()).finally(() => {
        giftPromise = null;
      });
    }
    return giftPromise;
  }

  // One bottle per order: drop extra bottles, re-add a missing one, remove it once the Welcome Kit product is gone.
  function reconcileGift(cartData) {
    const config = getGiftConfig(getGiftRoot());
    const items = cartData?.items || [];
    if (!config || giftPromise || !items.length) return;

    const giftLines = items.filter((item) => isGiftLine(item, config));
    const hasKit = items.some((item) => isWelcomeKitLine(item, config));
    const updates = {};

    giftLines.forEach((line, index) => {
      if (!hasKit || index > 0) updates[line.key] = 0;
      else if (line.quantity > 1) updates[line.key] = 1;
    });

    if (Object.keys(updates).length) {
      window.Alpine?.store("cart")?.updateLineItemsQuantities?.({ updates });
    } else if (hasKit && !giftLines.length) {
      addGift();
    }
  }

  /**
   * Every buybox add to cart (CTA, sticky bar, hidden submit) goes through the variant pack form's addToCart,
   * which redirects to checkout right after adding the product. Run it without the redirect, add the bottle,
   * then redirect, so the bottle is in the cart before checkout loads.
   */
  function patchFormAddToCart(root) {
    const form = root.querySelector(FORM_SELECTOR);
    if (!form || patchedForms.has(form)) return;

    let formData = null;
    try {
      formData = window.Alpine?.$data?.(form);
    } catch (_e) {
      formData = null;
    }
    const originalAddToCart = formData?.addToCart;
    if (typeof originalAddToCart !== "function") return;

    formData.addToCart = async function (options) {
      const redirect = Boolean(options?.redirectToCheckout);
      const offCanvasStore = redirect ? window.Alpine?.store("offCanvasCart") : null;
      const originalOpenOffcanvas = offCanvasStore?.openOffcanvas;
      if (offCanvasStore && originalOpenOffcanvas) offCanvasStore.openOffcanvas = () => {};

      try {
        const result = await originalAddToCart.call(this, { ...options, redirectToCheckout: false });
        await addGift(root);
        if (redirect) window.location.href = getCheckoutUrl();
        return result;
      } finally {
        if (offCanvasStore && originalOpenOffcanvas) offCanvasStore.openOffcanvas = originalOpenOffcanvas;
      }
    };
    patchedForms.add(form);
  }

  function patchAllForms() {
    document.querySelectorAll(GIFT_ROOT_SELECTOR).forEach(patchFormAddToCart);
  }

  function init() {
    document.querySelectorAll(PANEL_SELECTOR).forEach((panel) => {
      syncNextChargeDate(panel);
      watchFlavor(panel);
    });
    patchAllForms();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // The form's Alpine data may not exist yet on DOMContentLoaded; patch again once Alpine is up and right
  // before any click (capture phase runs ahead of the buttons' own handlers).
  document.addEventListener("alpine:initialized", patchAllForms);
  document.addEventListener("click", patchAllForms, true);
  document.addEventListener("cartReady", (event) => reconcileGift(event.detail?.cartData));

  window.ctrMlLfWelcomeKit = { addGift, reconcileGift };
})();
